import React from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ShieldCheck, Users, FileText, AlertTriangle, Sparkles } from 'lucide-react';
import MetricCard from '../components/MetricCard';

const mockData = {
  governanceScores: [
    { category: 'Board Independence', score: 90 },
    { category: 'Shareholder Rights', score: 85 },
    { category: 'Executive Compensation', score: 75 },
    { category: 'Audit & Risk Oversight', score: 95 },
    { category: 'Business Ethics', score: 80 },
  ],
  complianceIncidents: [
    { year: '2018', incidents: 5 },
    { year: '2019', incidents: 3 },
    { year: '2020', incidents: 2 },
    { year: '2021', incidents: 1 },
    { year: '2022', incidents: 0 },
  ],
  governanceScoreTrend: [
    { year: '2018', score: 78 },
    { year: '2019', score: 80 },
    { year: '2020', score: 82 },
    { year: '2021', score: 84 },
    { year: '2022', score: 85 },
  ],
};

const Governance = () => (
  <div className="p-6">
    <h1 className="text-3xl font-bold mb-6">Governance Oversight</h1>
   
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <MetricCard title="Governance Score" value="85/100" trend={2} icon={ShieldCheck} />
      <MetricCard title="Board Independence" value="90%" trend={5} icon={Users} />
      <MetricCard title="Policy Compliance" value="99.5%" trend={0.5} icon={FileText} />
      <MetricCard title="Risk Incidents" value="0" trend={-100} icon={AlertTriangle} />
    </div>

    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Governance Performance</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <RadarChart data={mockData.governanceScores}>
            <PolarGrid />
            <PolarAngleAxis dataKey="category" />
            <PolarRadiusAxis angle={30} domain={[0, 100]} />
            <Radar name="Score" dataKey="score" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
          </RadarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>

    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Compliance Incidents Trend</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={mockData.complianceIncidents}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Bar dataKey="incidents" fill="#82ca9d" />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>

    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Governance Score Trend</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={mockData.governanceScoreTrend}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis domain={[70, 90]} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="score" stroke="#8884d8" activeDot={{ r: 8 }} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>

    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Sparkles className="mr-2 h-5 w-5 text-yellow-500" />
          AI-Generated Governance Insights
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="list-disc pl-5 space-y-2">
          <li>Your governance score has shown consistent improvement over the past 5 years, with a 2% increase in the last year.</li>
          <li>Board independence is strong at 90%, which is above the industry average of 85%.</li>
          <li>The reduction in compliance incidents to zero in 2022 indicates effective risk management and compliance programs.</li>
          <li>While executive compensation scoring is good at 75%, there may be room for improvement in aligning it more closely with long-term company performance.</li>
          <li>Your audit and risk oversight score of 95% is exceptional, suggesting robust internal control systems.</li>
        </ul>
      </CardContent>
    </Card>
  </div>
);

export default Governance;